import { Injectable } from '@angular/core';
import { DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { FileSizePipe } from '../pipes/file-size.pipe';
import { IBANDisplayPipe } from '../pipes/IBAN-display.pipe';
import { ITableColumn } from '../models/table';
import { ColumnDefinitionFormatType } from '../../common/enums/column-definition-format-type';

@Injectable({
  providedIn: 'root'
})

export class DataFormatter {

  private readonly NUMERIC_FORMAT2DP: string = '1.2-2';
  private readonly NUMERIC_FORMAT4DP: string = '1.4-4';

  constructor(
    private readonly datePipe: DatePipe,
    private readonly decimalPipe: DecimalPipe,

    private readonly fileSizePipe: FileSizePipe,
    private readonly IBANDisplayPipe: IBANDisplayPipe) {
  }

  formatDate(value): string {
    return this.datePipe.transform(value, 'dd MMM yyyy');
  }

  formatDateUtc(value): string {
    return this.datePipe.transform(value, 'dd MMM yyyy');
  }

  formatShortDate(value): string {
    return this.datePipe.transform(value, 'dd/MM/yyyy');
  }

  formatDateTime(value): string {
    return this.datePipe.transform(value, 'dd MMM yyyy HH:mm:ss');
  }
  formatDateTimeUtcWithoutMilliseconds(value): string {
    return this.datePipe.transform(value, 'dd MMM yyyy HH:mm');
  }
  formatTime(value): string {
    return this.datePipe.transform(value, 'HH:mm');
  }

  formatNumeric(value): string {
    try {
      return this.decimalPipe.transform(value, this.NUMERIC_FORMAT2DP);
    } catch (error) {
      console.error(error);
      return `${value}`;
    }
  }

  formatNumeric4dP(value): string {
    try {
      return this.decimalPipe.transform(value, this.NUMERIC_FORMAT4DP);
    } catch (error) {
      console.error(error);
      return `${value}`;
    }
  }

  formatCurrency(value): string {
    return this.decimalPipe.transform(value, this.NUMERIC_FORMAT2DP);
  }

  formatFileSize(value): string {
    return this.fileSizePipe.transform(value, 2);
  }

  formatMeasurementUnit(value): string {
    switch(value) {
      case "Sq.m":
        return "Square metres";
      case "M.":
        return "Metres";
      case "Ft.":
        return "Feet";
      case "Sq.ft":
        return "Square feet";
      case "Cu.m":
        return "Cubic metres";
      case "Cu.ft":
        return "Cubic feet";
      case "Acres":
        return "Acres";
      case "Ha.":
        return "Hectares";
      default:
        return null;
    }
  }

  formatByType(rowData: any, formatType: ColumnDefinitionFormatType): string {
    let result: string;

    switch (formatType) {
      case ColumnDefinitionFormatType.IBAN:
      {
        result = this.IBANDisplayPipe.transform(rowData);
        break;
      }
      case ColumnDefinitionFormatType.Date:
        {
          result = this.formatDate(rowData);
          break;
        }
      case ColumnDefinitionFormatType.DateTime:
      {
        result = this.formatDateTime(rowData);
        break;
      }
      case ColumnDefinitionFormatType.DateTimeUtcWithoutMilliseconds:
      {
          result = this.formatDateTimeUtcWithoutMilliseconds(rowData);
        break;
      }
      case ColumnDefinitionFormatType.DateUtc:
      {
        result = this.formatDateUtc(rowData);
        break;
      }

      case ColumnDefinitionFormatType.YesNo:
        {
          const lowerCase = `${rowData}`.toLowerCase();
          if (lowerCase === 'y' || lowerCase === 'yes ') {
            result = 'Yes';
          } else if (lowerCase === 'n' || lowerCase === 'no') {
            result = 'No';
          } else {
            result = rowData;
          }
          break;
        }
      case ColumnDefinitionFormatType.Numeric2Dp:
        {
          result = this.formatNumeric(rowData);
          break;
        }
      case ColumnDefinitionFormatType.Currency:
        {
          result = this.formatCurrency(rowData); // get from  currency code?
          break;
        }
      case ColumnDefinitionFormatType.FileSizeFromBytes:
        {
          result = this.formatFileSize(rowData);
          break;
        }
      case ColumnDefinitionFormatType.Time:
        {
          result = this.formatTime(rowData);
          break;
        }
      case ColumnDefinitionFormatType.MeasurementUnit:
      {
          result = this.formatMeasurementUnit(rowData);
          break;
      }
      case ColumnDefinitionFormatType.Numeric4Dp:
      {
        result = this.formatNumeric4dP(rowData);
        break;
      }
      default:
        {
          result = rowData;
          break;
        }
    }
    return result ? `${result}` : '';
  }

  formatByColumnType(rowData, columnDefinition: ITableColumn): string {
    return this.formatByType(rowData, columnDefinition.Type);
  }
}
